import React from 'react';
import { useTheme } from '@/context/ThemeContext';
import { X, Bell, Check } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationPanel({ open, onClose, notifications, onRefresh, onTorRefresh }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleMarkRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      onRefresh();
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="notification-backdrop"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed right-0 top-0 bottom-0 z-50 w-full max-w-sm border-l ${
              isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'
            }`}
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <div className="flex items-center gap-2">
                <Bell size={18} weight="bold" className="text-yellow-400" />
                <h2 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
                  Notifications
                </h2>
              </div>
              <button
                onClick={onClose}
                className={`p-1.5 rounded-sm transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-600'}`}
                data-testid="close-notifications"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* List */}
            <div className="overflow-y-auto h-[calc(100vh-57px)]">
              {notifications.length === 0 ? (
                <div className={`p-8 text-center font-ibm text-sm ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                  No notifications yet
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.notification_id}
                    className={`p-4 border-b transition-colors ${
                      isDark ? 'border-zinc-800' : 'border-zinc-200'
                    } ${!notif.read ? (isDark ? 'bg-yellow-400/5' : 'bg-yellow-50') : ''}`}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 mt-1.5 rounded-full shrink-0 ${notif.read ? 'bg-zinc-600' : 'bg-yellow-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`font-ibm text-sm font-medium mb-0.5 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
                          {notif.title}
                        </p>
                        <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                          {notif.message}
                        </p>
                        <p className={`font-ibm text-[10px] mt-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={() => handleMarkRead(notif.notification_id)}
                          className={`p-1 rounded-sm shrink-0 transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-500' : 'hover:bg-zinc-100 text-zinc-400'}`}
                          title="Mark as read"
                          data-testid={`mark-read-${notif.notification_id}`}
                        >
                          <Check size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
